import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Check, RotateCcw, Move } from "lucide-react";

/**
 * Manuel köşe belirleme bileşeni
 * Kullanıcı 4 köşeyi sürükleyerek dokümanın sınırlarını belirleyebilir
 */
export default function CropEditor({ 
  imageData, 
  onConfirm, 
  onCancel,
  initialCorners = null 
}) {
  const containerRef = useRef(null);
  const [corners, setCorners] = useState(null);
  const [draggingCorner, setDraggingCorner] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Görüntü yüklendiğinde varsayılan köşeleri ayarla
  useEffect(() => {
    if (!imageData) return;

    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
      
      // Sabit container genişliği kullan (dialog genişliği yaklaşık 800px, padding çıkınca 700px)
      const containerWidth = 700;
      const containerHeight = 500;
      
      // Ölçek hesapla
      const scaleX = containerWidth / img.width;
      const scaleY = containerHeight / img.height;
      const newScale = Math.min(scaleX, scaleY, 1);
      
      setScale(newScale);
      setContainerSize({
        width: img.width * newScale,
        height: img.height * newScale
      });

      // Varsayılan köşeler (görüntünün %10 içinde)
      const margin = 0.1;
      const defaultCorners = initialCorners || [
        { x: img.width * margin, y: img.height * margin }, // Sol üst
        { x: img.width * (1 - margin), y: img.height * margin }, // Sağ üst
        { x: img.width * (1 - margin), y: img.height * (1 - margin) }, // Sağ alt
        { x: img.width * margin, y: img.height * (1 - margin) }, // Sol alt
      ];
      
      setCorners(defaultCorners);
    };
    
    img.onerror = (err) => {
      console.error("CropEditor: Image load error", err);
    };
    
    img.src = imageData;
  }, [imageData, initialCorners]);

  // Koordinatları ekran koordinatlarına çevir
  const toScreenCoords = useCallback((point) => {
    return {
      x: point.x * scale,
      y: point.y * scale
    };
  }, [scale]);

  // Ekran koordinatlarını görüntü koordinatlarına çevir
  const toImageCoords = useCallback((point) => {
    return {
      x: point.x / scale,
      y: point.y / scale
    };
  }, [scale]);

  // En yakın köşeyi bul (bölge tıklama için)
  const findNearestCorner = useCallback((x, y) => {
    if (!corners) return null;
    
    const screenCorners = corners.map(toScreenCoords);
    let nearestIndex = null;
    let minDistance = Infinity;
    
    // Her köşe için mesafeyi hesapla
    screenCorners.forEach((corner, index) => {
      const distance = Math.sqrt(
        Math.pow(corner.x - x, 2) + Math.pow(corner.y - y, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });
    
    // Eğer en yakın köşe 150px'den yakınsa o köşeyi seç
    if (minDistance < 150) {
      return nearestIndex;
    }
    
    return null;
  }, [corners, toScreenCoords]);

  // Container'a tıklama - en yakın köşeyi seç
  const handleContainerMouseDown = (e) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const nearestCorner = findNearestCorner(x, y);
    if (nearestCorner !== null) {
      e.preventDefault();
      setDraggingCorner(nearestCorner);
    }
  };

  // Container'a touch başlat
  const handleContainerTouchStart = (e) => {
    if (!containerRef.current) return;
    
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    const nearestCorner = findNearestCorner(x, y);
    if (nearestCorner !== null) {
      e.preventDefault();
      setDraggingCorner(nearestCorner);
    }
  };

  // Köşe sürükleme başlat
  const handleCornerMouseDown = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingCorner(index);
  };

  // Köşe sürükleme (mouse move)
  const handleMouseMove = useCallback((e) => {
    if (draggingCorner === null || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Sınırları kontrol et
    const clampedX = Math.max(0, Math.min(x, containerSize.width));
    const clampedY = Math.max(0, Math.min(y, containerSize.height));

    // Görüntü koordinatlarına çevir
    const imageCoords = toImageCoords({ x: clampedX, y: clampedY });

    setCorners(prev => {
      const newCorners = [...prev];
      newCorners[draggingCorner] = imageCoords;
      return newCorners;
    });
  }, [draggingCorner, containerSize, toImageCoords]);

  // Köşe sürükleme bitir
  const handleMouseUp = useCallback(() => {
    setDraggingCorner(null);
  }, []);

  // Touch events for mobile
  const handleTouchStart = (index, e) => {
    e.preventDefault();
    setDraggingCorner(index);
  };

  const handleTouchMove = useCallback((e) => {
    if (draggingCorner === null || !containerRef.current) return;

    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    const clampedX = Math.max(0, Math.min(x, containerSize.width));
    const clampedY = Math.max(0, Math.min(y, containerSize.height));

    const imageCoords = toImageCoords({ x: clampedX, y: clampedY });

    setCorners(prev => {
      const newCorners = [...prev];
      newCorners[draggingCorner] = imageCoords;
      return newCorners;
    });
  }, [draggingCorner, containerSize, toImageCoords]);

  const handleTouchEnd = useCallback(() => {
    setDraggingCorner(null);
  }, []);

  // Event listeners
  useEffect(() => {
    if (draggingCorner !== null) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [draggingCorner, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Köşeleri sıfırla
  const resetCorners = () => {
    if (!imageSize.width) return;
    
    const margin = 0.05;
    setCorners([
      { x: imageSize.width * margin, y: imageSize.height * margin },
      { x: imageSize.width * (1 - margin), y: imageSize.height * margin },
      { x: imageSize.width * (1 - margin), y: imageSize.height * (1 - margin) },
      { x: imageSize.width * margin, y: imageSize.height * (1 - margin) },
    ]);
  };

  // Onay
  const handleConfirm = () => {
    if (corners && onConfirm) {
      // Köşeleri saat yönünde sırala (sol-üst, sağ-üst, sağ-alt, sol-alt)
      onConfirm(corners);
    }
  };

  // Polygon path oluştur
  const getPolygonPath = () => {
    if (!corners) return "";
    const screenCorners = corners.map(toScreenCoords);
    return screenCorners.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ') + ' Z';
  };

  // Görüntü veya köşeler yoksa loading göster
  if (!imageData) {
    return null;
  }

  // Köşeler henüz hesaplanmadıysa loading göster
  if (!corners) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center p-8 bg-slate-100 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c11718]"></div>
          <span className="ml-3 text-slate-600">Bild wird geladen...</span>
        </div>
      </div>
    );
  }

  const screenCorners = corners.map(toScreenCoords);
  const cornerLabels = ['Sol Üst', 'Sağ Üst', 'Sağ Alt', 'Sol Alt'];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          <Move className="h-4 w-4 inline mr-1" />
          Köşeye yakın bir yere tıklayarak sürükleyin
        </p>
        <Button variant="ghost" size="sm" onClick={resetCorners}>
          <RotateCcw className="h-4 w-4 mr-1" />
          Sıfırla
        </Button>
      </div>

      {/* Görüntü ve Overlay Container */}
      <div 
        ref={containerRef}
        className="relative bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center cursor-crosshair"
        style={{ minHeight: '400px' }}
        onMouseDown={handleContainerMouseDown}
        onTouchStart={handleContainerTouchStart}
      >
        <div 
          className="relative"
          style={{ 
            width: containerSize.width, 
            height: containerSize.height,
          }}
        >
          {/* Görüntü */}
          <img 
            src={imageData} 
            alt="Crop preview"
            className="absolute top-0 left-0"
            style={{ 
              width: containerSize.width, 
              height: containerSize.height,
              pointerEvents: 'none'
            }}
            draggable={false}
          />

          {/* Karartma Overlay - seçili alanın dışı */}
          <svg 
            className="absolute top-0 left-0"
            width={containerSize.width}
            height={containerSize.height}
            style={{ pointerEvents: 'none' }}
          >
            {/* Dış karartma */}
            <defs>
              <mask id="cropMask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <path d={getPolygonPath()} fill="black" />
              </mask>
            </defs>
            <rect 
              x="0" y="0" 
              width="100%" height="100%" 
              fill="rgba(0,0,0,0.6)" 
              mask="url(#cropMask)"
            />

            {/* Seçim çerçevesi */}
            <path 
              d={getPolygonPath()} 
              fill="none" 
              stroke="#c11718" 
              strokeWidth="2"
              strokeDasharray="8,4"
            />

            {/* Köşeler arası çizgiler */}
            {screenCorners.map((corner, i) => {
              const nextCorner = screenCorners[(i + 1) % 4];
              return (
                <line
                  key={`line-${i}`}
                  x1={corner.x}
                  y1={corner.y}
                  x2={nextCorner.x}
                  y2={nextCorner.y}
                  stroke="#c11718"
                  strokeWidth="2"
                />
              );
            })}
          </svg>

          {/* Köşe noktaları (sürüklenebilir) - Yarı saydam */}
          {screenCorners.map((corner, index) => (
            <div
              key={index}
              className={`absolute cursor-move transform -translate-x-1/2 -translate-y-1/2 
                ${draggingCorner === index ? 'z-50' : 'z-40'}
              `}
              style={{
                left: corner.x,
                top: corner.y,
              }}
              onMouseDown={(e) => handleCornerMouseDown(index, e)}
              onTouchStart={(e) => handleTouchStart(index, e)}
            >
              {/* Yarı saydam köşe topu */}
              <div 
                className={`
                  w-8 h-8 rounded-full 
                  flex items-center justify-center
                  transition-all duration-150
                  ${draggingCorner === index 
                    ? 'scale-150 bg-[#c11718]/90 border-2 border-white shadow-xl' 
                    : 'bg-[#c11718]/40 border-2 border-white/70 hover:bg-[#c11718]/60 hover:scale-110'}
                  backdrop-blur-sm
                `}
              >
                {/* İç nokta - aktifken görünür */}
                <div className={`w-2 h-2 rounded-full transition-opacity ${draggingCorner === index ? 'bg-white opacity-100' : 'bg-white/50 opacity-70'}`} />
              </div>
              
              {/* Köşe etiketi - sadece aktifken veya hover'da görünür */}
              <div 
                className={`
                  absolute whitespace-nowrap text-xs font-medium px-2 py-1 
                  bg-black/70 text-white rounded
                  transition-opacity duration-150
                  ${draggingCorner === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                  ${index === 0 ? 'left-full ml-2' : ''}
                  ${index === 1 ? 'right-full mr-2' : ''}
                  ${index === 2 ? 'right-full mr-2' : ''}
                  ${index === 3 ? 'left-full ml-2' : ''}
                  pointer-events-none
                `}
                style={{
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                {cornerLabels[index]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Butonlar */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          İptal
        </Button>
        <Button onClick={handleConfirm} className="bg-[#c11718] hover:bg-[#a11415]">
          <Check className="h-4 w-4 mr-2" />
          Onayla
        </Button>
      </div>
    </div>
  );
}
